/**
 * useIsMeetingPast — determine whether a meeting's reference time has elapsed.
 *
 * The "is past" decision only changes once: when the wall clock crosses the
 * reference time. Instead of recomputing on every parent render, this hook
 * schedules a one-shot timer for that exact moment. When the timer fires the
 * memo recomputes with a fresh `Date.now()` reading.
 *
 * Behavior:
 * - Missing or invalid `referenceTime` → `false`, no timer scheduled.
 * - Reference time already past → `true` on mount, no timer scheduled.
 * - Reference time within ~23 days → timer scheduled; transitions to `true`
 *   ~1s after the reference time elapses.
 * - Reference time more than ~23 days away → `false`, no timer scheduled
 *   (avoids `setTimeout`'s ~24.8-day overflow; the user will reload before
 *   such a meeting becomes past).
 */
import { useEffect, useMemo, useState } from 'react';

/**
 * Maximum scheduling window in ms. `setTimeout` rolls over for delays >
 * 2^31-1 ms (~24.8 days), so we cap below that and accept that very-far-future
 * meetings won't auto-flip without a reload.
 */
const MAX_SCHEDULE_MS = 2_000_000_000; // ~23 days

export function useIsMeetingPast(
  referenceTime: string | null | undefined
): boolean {
  const referenceDate = useMemo(() => {
    if (!referenceTime) {
      return null;
    }
    const d = new Date(referenceTime);
    return isNaN(d.getTime()) ? null : d;
  }, [referenceTime]);

  // `tick` is incremented when the timer fires; it forces the memo below to
  // recompute with a fresh `Date.now()` reading.
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!referenceDate) {
      return;
    }
    const delta = referenceDate.getTime() - Date.now();
    if (delta <= 0 || delta > MAX_SCHEDULE_MS) {
      return;
    }
    // Fire 1s after the reference moment to avoid a sub-millisecond race
    // where the timer fires but Date.now() still rounds below referenceDate.
    const id = setTimeout(() => setTick(t => t + 1), delta + 1000);
    return () => clearTimeout(id);
  }, [referenceDate]);

  return useMemo(
    () => (referenceDate ? referenceDate.getTime() <= Date.now() : false),
    // `tick` forces recomputation when the timer fires; `Date.now()` itself
    // isn't a binding and can't appear in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [referenceDate, tick]
  );
}
